import { useMemo } from 'react';
import dagre from 'dagre';
import type { Node, Edge } from 'reactflow';
import { MarkerType } from 'reactflow';
import { tokens } from '@fluentui/react-components';
import type { PhysicalTable, SchemaRelationship } from './types';
import type { SchemaTableNodeData } from './SchemaTableNode';

const NODE_WIDTH = 260;
const NODE_BASE_HEIGHT = 80;
const ROW_HEIGHT = 20;
const MAX_VISIBLE = 7;

function estimateNodeHeight(columnCount: number): number {
  const visible = Math.min(columnCount, MAX_VISIBLE);
  const hasFooter = columnCount > MAX_VISIBLE ? 28 : 0;
  return NODE_BASE_HEIGHT + visible * ROW_HEIGHT + hasFooter;
}

function getCardinalityLabel(type: string): string {
  if (type.includes('one_to_many') || type.includes('1:N')) return '1:N';
  if (type.includes('many_to_one') || type.includes('N:1')) return 'N:1';
  if (type.includes('one_to_one') || type.includes('1:1')) return '1:1';
  return '';
}

function getEdgeLabel(rel: SchemaRelationship): string {
  const cardinality = getCardinalityLabel(rel.relationshipType);
  const columnInfo = `${rel.fromColumn} → ${rel.toColumn}`;
  return cardinality ? `${cardinality}\n${columnInfo}` : columnInfo;
}

export interface UseSchemaLayoutOptions {
  tables: PhysicalTable[];
  relationships: SchemaRelationship[];
  selectedTableId: string | null;
  highlightedTableIds: Set<string>;
}

export function useSchemaLayout({
  tables,
  relationships,
  selectedTableId,
  highlightedTableIds,
}: UseSchemaLayoutOptions): { nodes: Node<SchemaTableNodeData>[]; edges: Edge[] } {
  return useMemo(() => {
    if (tables.length === 0) return { nodes: [], edges: [] };

    const hasSelection = selectedTableId !== null || highlightedTableIds.size > 0;

    // Build dagre graph
    const g = new dagre.graphlib.Graph();
    g.setDefaultEdgeLabel(() => ({}));
    g.setGraph({ rankdir: 'LR', nodesep: 60, ranksep: 140 });

    for (const table of tables) {
      g.setNode(table.id, {
        width: NODE_WIDTH,
        height: estimateNodeHeight(table.columns.length),
      });
    }

    for (const rel of relationships) {
      g.setEdge(rel.fromTableId, rel.toTableId);
    }

    dagre.layout(g);

    // Convert to React Flow nodes
    const nodes: Node<SchemaTableNodeData>[] = tables.map((table) => {
      const pos = g.node(table.id);
      const isSelected = selectedTableId === table.id;
      // For table selection: connected tables are highlighted but NOT the selected one
      const isHighlighted = !isSelected && highlightedTableIds.has(table.id);
      const isFaded = hasSelection && !isSelected && !isHighlighted;

      return {
        id: table.id,
        type: 'schemaTable',
        position: { x: pos.x - NODE_WIDTH / 2, y: pos.y - pos.height / 2 },
        data: {
          label: table.aliasName || table.displayName || table.name,
          columns: table.columns,
          connectionName: table.connectionName,
          rowCount: table.rowCount,
          isHighlighted,
          isFaded,
          isSelected,
        },
      };
    });

    // Convert to React Flow edges
    const edges: Edge[] = relationships.map((rel) => {
      const isRelHighlighted =
        highlightedTableIds.has(rel.fromTableId) && highlightedTableIds.has(rel.toTableId);
      const involvesSelected =
        rel.fromTableId === selectedTableId || rel.toTableId === selectedTableId;
      const isActive = isRelHighlighted || involvesSelected;
      const isFaded = hasSelection && !isActive;

      // Dashed for inferred/polymorphic, solid for physical FK
      const isInferred = rel.status === 'proposed' || rel.confidence < 0.8;

      return {
        id: rel.id,
        source: rel.fromTableId,
        target: rel.toTableId,
        type: 'smoothstep',
        animated: false,
        label: getEdgeLabel(rel),
        style: {
          stroke: isActive ? tokens.colorBrandStroke1 : isFaded ? tokens.colorNeutralStroke3 : tokens.colorNeutralStroke1,
          strokeWidth: isActive ? 2.5 : 1.5,
          opacity: isFaded ? 0.2 : 1,
          strokeDasharray: isInferred ? '6 3' : undefined,
        },
        labelStyle: {
          fontSize: 9,
          fill: isActive ? tokens.colorBrandForeground1 : tokens.colorNeutralForeground3,
          fontWeight: isActive ? 600 : 400,
        },
        labelBgStyle: { fill: tokens.colorNeutralBackground1, fillOpacity: 0.85 },
        labelBgPadding: [4, 2] as [number, number],
        markerEnd: { type: MarkerType.ArrowClosed, width: 10, height: 10 },
        data: {
          tooltip: `${rel.fromTableName}.${rel.fromColumn} → ${rel.toTableName}.${rel.toColumn}\nType: ${rel.relationshipType}\nJoin: ${rel.joinType}\nConfidence: ${Math.round(rel.confidence * 100)}%\nStatus: ${rel.status}`,
        },
      };
    });

    return { nodes, edges };
  }, [tables, relationships, selectedTableId, highlightedTableIds]);
}